/**
 * ============================================================================
 * HOMECROWD — Early Access Program Overview (school-scoped)
 * ============================================================================
 * Route: /early-access-overview/:schoolId/
 * Customizes copy, colors, and rewards naming based on the school.
 * ============================================================================
 */
import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import apiService from "../services/api";
import walkthroughSignup from "../assets/images/sign-up.png";
import walkthroughLinking from "../assets/images/add-a-card.png";
import walkthroughTracking from "../assets/images/leaderboard.png";

const HC_LOGO_DARK = "data:image/png;base64,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";

const DARK = "#161616";
const DEFAULT_COLOR = "#E00122";

const SchemeContext = createContext(null);
const useScheme = () => useContext(SchemeContext);

function hexToRgb(hex) {
  const h = hex.replace("#", "");
  return {
    r: parseInt(h.substring(0, 2), 16),
    g: parseInt(h.substring(2, 4), 16),
    b: parseInt(h.substring(4, 6), 16),
  };
}
function rgbToHex(r, g, b) {
  const toHex = (n) => Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, "0");
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}
function relativeLuminance({ r, g, b }) {
  const ch = (v) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * ch(r) + 0.7152 * ch(g) + 0.0722 * ch(b);
}
function mixWithWhite({ r, g, b }, amount) {
  return { r: r + (255 - r) * amount, g: g + (255 - g) * amount, b: b + (255 - b) * amount };
}
// Return a readable variant of `color` against the dark hero/section backgrounds.
// If the source color is too dark (low luminance), lighten it until it reads cleanly.
function accentForDark(color) {
  try {
    const rgb = hexToRgb(color);
    let lum = relativeLuminance(rgb);
    if (lum >= 0.25) return color; // already bright enough
    let mixed = rgb;
    let mix = 0.35;
    while (mix <= 0.9) {
      mixed = mixWithWhite(rgb, mix);
      if (relativeLuminance(mixed) >= 0.28) break;
      mix += 0.1;
    }
    return rgbToHex(mixed.r, mixed.g, mixed.b);
  } catch {
    return color;
  }
}

function markFromName(name) {
  const skip = new Set(["of", "the", "and", "for", "at", "in", "on", "by", "to", "a", "an"]);
  const words = String(name || "").trim().split(/\s+/).filter(w => w && !skip.has(w.toLowerCase()));
  let letters = "";
  for (const w of words) { letters += w[0].toUpperCase(); if (letters.length >= 3) break; }
  if (letters.length >= 2) return letters;
  return String(name || "").replace(/\s+/g, "").slice(0, 2).toUpperCase();
}

function buildScheme(school) {
  const name = school?.name || "Your School Athletics";
  const mascot = school?.mascot && String(school.mascot).trim();
  const short = mascot || name.split(" ")[0];
  const color = (school?.primary_color && /^#?[0-9a-fA-F]{6}$/.test(school.primary_color.replace("#", ""))
    ? (school.primary_color.startsWith("#") ? school.primary_color : `#${school.primary_color}`)
    : DEFAULT_COLOR);
  const accent = accentForDark(color);
  const rewardsTitle = school?.rewards_title || `${name} Rewards`;
  const image = school?.image || null;
  const mark = markFromName(name);
  return { name, short, color, accent, rewardsTitle, image, mark };
}

function buildStyles(color) {
  return `
    @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700;1,9..40,400&family=Fraunces:ital,opsz,wght@0,9..144,600;0,9..144,700;0,9..144,800;1,9..144,500&display=swap');
    :root { --c: ${color}; --d: ${DARK}; --fd: 'Fraunces', Georgia, serif; --fb: 'DM Sans', system-ui, sans-serif; }
    * { margin:0; padding:0; box-sizing:border-box; }
    html { scroll-behavior:smooth; }
    .fs { opacity:0; transform:translateY(24px); transition:opacity .7s ease, transform .7s ease; }
    .fs.v { opacity:1; transform:translateY(0); }
  `;
}

function useFade() {
  const r = useRef(null);
  useEffect(() => {
    const el = r.current; if (!el) return;
    const o = new IntersectionObserver(([e]) => { if (e.isIntersecting) el.classList.add("v"); }, { threshold: 0.12 });
    o.observe(el); return () => o.disconnect();
  }, []); return r;
}

const Tag = ({ children, light }) => {
  const { color, accent } = useScheme();
  return <p className="text-sm font-semibold uppercase tracking-widest mb-3" style={{ color: light ? accent : color }}>{children}</p>;
};
const H2 = ({ children, light }) => (
  <h2 className={`text-3xl md:text-4xl font-bold leading-tight mb-4 ${light ? "text-white" : "text-gray-900"}`} style={{ fontFamily: "var(--fd)" }}>{children}</h2>
);
const Sub = ({ children, light }) => (
  <p className={`text-lg leading-relaxed ${light ? "text-white/60" : "text-gray-600"}`}>{children}</p>
);
const Placeholder = ({ label, dark, img, alt }) => {
  if (img) {
    return (
      <div className="flex justify-center">
        <img src={img} alt={alt || label || ""} className="block max-h-[28rem] w-auto rounded-2xl" />
      </div>
    );
  }
  return (
    <div className={`rounded-2xl border ${dark ? "border-white/10" : "border-gray-200 bg-gray-50"} flex items-center justify-center p-8 min-h-48`}>
      <p className={`text-sm font-mono ${dark ? "text-white/30" : "text-gray-400"}`}>{label}</p>
    </div>
  );
};


/* ══════════════════════════════════════════════════════════════════════════ */
/*  NAV (white bar, sits above the hero)                                    */
/* ══════════════════════════════════════════════════════════════════════════ */
function Nav() {
  const { name, color, image, mark } = useScheme();
  return (
    <nav className="bg-white border-b border-gray-200 shadow-sm">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center gap-4">
            <img src={HC_LOGO_DARK} alt="Homecrowd" className="h-8 w-auto object-contain" />
            <div className="hidden sm:block w-px h-8 bg-gray-300" />
            <span className="hidden sm:block text-sm text-gray-500">Early Access Program</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              {image ? (
                <img
                  src={image}
                  alt={`${name} logo`}
                  className="h-8 w-auto object-contain"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                  }}
                />
              ) : null}
              <div
                className="w-8 h-8 rounded-full items-center justify-center text-xs font-bold bg-gray-50 border border-gray-300"
                style={{ color, display: image ? 'none' : 'flex' }}
              >
                {mark}
              </div>
              <span className="font-medium text-gray-900 text-sm">{name}</span>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
}


/* ══════════════════════════════════════════════════════════════════════════ */
/*  1. HERO                                                                  */
/* ══════════════════════════════════════════════════════════════════════════ */
function Hero() {
  const { name, short, color, accent, rewardsTitle } = useScheme();
  return (
    <section className="relative overflow-hidden" style={{ fontFamily:"var(--fb)", background:`linear-gradient(145deg, ${DARK} 0%, #232323 60%, ${accent}22 100%)`, minHeight:"calc(100vh - 65px)" }}>
      <div className="absolute inset-0 opacity-5" style={{ backgroundImage:"linear-gradient(rgba(255,255,255,.1) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.1) 1px,transparent 1px)", backgroundSize:"64px 64px" }} />
      <div className="relative z-10 max-w-6xl mx-auto px-6 md:px-10 pt-16 pb-24 md:pt-20 md:pb-32">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-medium mb-8" style={{ background:`${accent}1f`, color:accent, border:`1px solid ${accent}40` }}>
            <span className="w-2 h-2 rounded-full" style={{ background:accent }} />
            {name} — Early Access Program
          </div>
          <h1 className="text-white text-4xl md:text-6xl lg:text-7xl font-bold leading-tight mb-6" style={{ fontFamily:"var(--fd)", letterSpacing:"-0.02em" }}>
            Turn everyday spending into{" "}
            <span style={{ color:accent }}>{rewardsTitle}</span>.
          </h1>
          <p className="text-white/70 text-lg md:text-xl leading-relaxed max-w-xl mb-4">
            Homecrowd is a fan engagement platform. Link your card for in-person spending, add the browser extension for online shopping, and earn {rewardsTitle} that unlock exclusive access and experiences with {name}.
          </p>
          <p className="text-white/40 text-sm mb-10">
            This overview is for early testers in the {short} program.
          </p>
          <a href="#what-we-need" className="inline-flex items-center justify-center rounded-lg px-8 py-4 text-white font-semibold text-base transition-all duration-200 hover:brightness-110 hover:scale-105" style={{ background:color }}>
            How to Get Started
          </a>
        </div>
      </div>
      <div className="absolute bottom-0 left-0 right-0 h-px" style={{ background:`linear-gradient(90deg,transparent,${accent}60,transparent)` }} />
    </section>
  );
}


/* ══════════════════════════════════════════════════════════════════════════ */
/*  2. THE BIG IDEA + ECOSYSTEM DIAGRAM (SVG)                               */
/* ══════════════════════════════════════════════════════════════════════════ */
function BigIdea() {
  const { short, color, rewardsTitle } = useScheme();
  const ref = useFade();

  const NODES = { fans:{ x:370, y:90 }, biz:{ x:140, y:350 }, sch:{ x:600, y:350 } };
  const R = 75;
  const START_PAD = 8;  // clearance from origin circle
  const END_PAD = 2;    // small clearance so arrow tip reaches the target circle
  const START_DIST = R + START_PAD;
  const END_DIST = R + END_PAD;
  const GAP = 10;
  const LABEL_PAD = 18;
  const makeEdge = (from, to, offset) => {
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const len = Math.hypot(dx, dy);
    const ux = dx / len, uy = dy / len;
    const px = -uy, py = ux; // 90° CCW perpendicular unit
    const x1 = from.x + ux * START_DIST + px * offset;
    const y1 = from.y + uy * START_DIST + py * offset;
    const x2 = to.x   - ux * END_DIST   + px * offset;
    const y2 = to.y   - uy * END_DIST   + py * offset;
    const mx = (x1 + x2) / 2;
    const my = (y1 + y2) / 2;
    let angle = Math.atan2(dy, dx) * 180 / Math.PI;
    if (angle > 90 || angle < -90) angle += 180;
    const lx = mx + px * LABEL_PAD;
    const ly = my + py * LABEL_PAD;
    return { x1, y1, x2, y2, lx, ly, angle };
  };
  const fansToBiz = makeEdge(NODES.fans, NODES.biz, GAP);
  const bizToFans = makeEdge(NODES.biz, NODES.fans, GAP);
  const bizToSch  = makeEdge(NODES.biz, NODES.sch, GAP);
  const schToBiz  = makeEdge(NODES.sch, NODES.biz, GAP);
  const fansToSch = makeEdge(NODES.fans, NODES.sch, GAP);
  const schToFans = makeEdge(NODES.sch, NODES.fans, GAP);
  return (
    <section ref={ref} className="fs py-20 md:py-28 px-6 md:px-10" style={{ fontFamily:"var(--fb)", background:"#fff" }}>
      <div className="max-w-5xl mx-auto">
        <Tag>The Big Idea</Tag>
        <H2>Your everyday spending earns access and experiences with {short}.</H2>
        <Sub>Homecrowd creates a loop where everyone wins: fans get access and experiences, businesses get community connection, and the school gets deeper engagement.</Sub>

        <div className="grid md:grid-cols-3 gap-6 mt-12 mb-12">
          {[
            { icon:"🎓", title:"Fans", desc:`Spend at participating businesses — in person or online — and earn ${rewardsTitle} that unlock exclusive access and experiences within the program.` },
            { icon:"🍽️", title:"Local Businesses", desc:"Opt in because they want to be part of the community. They fund the program through participation — only when fans actually spend." },
            { icon:"🏟️", title:"Schools", desc:"Get measurable fan engagement, deeper community ties, and a revenue-aligned ecosystem around the program." },
          ].map((c,i) => (
            <div key={i} className="rounded-xl border border-gray-100 p-6 bg-gray-50">
              <span className="text-3xl mb-4 block">{c.icon}</span>
              <h3 className="text-lg font-bold text-gray-900 mb-2" style={{ fontFamily:"var(--fd)" }}>{c.title}</h3>
              <p className="text-gray-600 text-sm leading-relaxed">{c.desc}</p>
            </div>
          ))}
        </div>

        <div className="bg-gray-50 rounded-2xl border border-gray-100 p-6 md:p-10">
          <svg viewBox="0 0 740 440" xmlns="http://www.w3.org/2000/svg" className="w-full max-w-2xl mx-auto">
            <defs>
              <marker id="arr" markerWidth="10" markerHeight="7" refX="10" refY="3.5" orient="auto">
                <path d="M 0 0 L 10 3.5 L 0 7 Z" fill="#9ca3af" />
              </marker>
              <marker id="arrR" markerWidth="10" markerHeight="7" refX="10" refY="3.5" orient="auto">
                <path d="M 0 0 L 10 3.5 L 0 7 Z" fill={color} />
              </marker>
            </defs>

            <circle cx="370" cy="90" r="75" fill={`${color}08`} stroke={color} strokeWidth="2.5" />
            <text x="370" y="85" textAnchor="middle" fill={color} fontWeight="700" fontSize="20" fontFamily="var(--fd)">Fans</text>
            <text x="370" y="108" textAnchor="middle" fill="#6b7280" fontSize="11" fontFamily="var(--fb)">Proximity to your team</text>

            <circle cx="140" cy="350" r="75" fill="#f3f4f6" stroke="#d1d5db" strokeWidth="2" />
            <text x="140" y="345" textAnchor="middle" fill="#374151" fontWeight="700" fontSize="20" fontFamily="var(--fd)">Businesses</text>
            <text x="140" y="366" textAnchor="middle" fill="#6b7280" fontSize="11" fontFamily="var(--fb)">Get foot traffic</text>

            <circle cx="600" cy="350" r="75" fill="#f3f4f6" stroke="#d1d5db" strokeWidth="2" />
            <text x="600" y="345" textAnchor="middle" fill="#374151" fontWeight="700" fontSize="20" fontFamily="var(--fd)">School</text>
            <text x="600" y="366" textAnchor="middle" fill="#6b7280" fontSize="11" fontFamily="var(--fb)">Engagement & revenue</text>

            {/* Fans ↔ Businesses (left edge) */}
            <line x1={fansToBiz.x1} y1={fansToBiz.y1} x2={fansToBiz.x2} y2={fansToBiz.y2} stroke="#9ca3af" strokeWidth="2" markerEnd="url(#arr)" />
            <text x={fansToBiz.lx} y={fansToBiz.ly} fill="#9ca3af" fontSize="11" fontFamily="var(--fb)" textAnchor="middle" dominantBaseline="central" transform={`rotate(${fansToBiz.angle}, ${fansToBiz.lx}, ${fansToBiz.ly})`}>Spend like normal</text>

            <line x1={bizToFans.x1} y1={bizToFans.y1} x2={bizToFans.x2} y2={bizToFans.y2} stroke={color} strokeWidth="2" strokeDasharray="5 4" markerEnd="url(#arrR)" />
            <text x={bizToFans.lx} y={bizToFans.ly} fill={color} fontSize="11" fontFamily="var(--fb)" textAnchor="middle" dominantBaseline="central" transform={`rotate(${bizToFans.angle}, ${bizToFans.lx}, ${bizToFans.ly})`}>Earn rewards</text>

            {/* Businesses ↔ School (bottom edge) */}
            <line x1={bizToSch.x1} y1={bizToSch.y1} x2={bizToSch.x2} y2={bizToSch.y2} stroke="#9ca3af" strokeWidth="2" markerEnd="url(#arr)" />
            <text x={bizToSch.lx} y={bizToSch.ly} fill="#9ca3af" fontSize="11" fontFamily="var(--fb)" textAnchor="middle" dominantBaseline="central">Revenue alignment</text>

            <line x1={schToBiz.x1} y1={schToBiz.y1} x2={schToBiz.x2} y2={schToBiz.y2} stroke={color} strokeWidth="2" strokeDasharray="5 4" markerEnd="url(#arrR)" />
            <text x={schToBiz.lx} y={schToBiz.ly} fill={color} fontSize="11" fontFamily="var(--fb)" textAnchor="middle" dominantBaseline="central">Impressions for their brand</text>

            {/* Fans ↔ School (right edge) */}
            <line x1={schToFans.x1} y1={schToFans.y1} x2={schToFans.x2} y2={schToFans.y2} stroke={color} strokeWidth="2" strokeDasharray="5 4" markerEnd="url(#arrR)" />
            <text x={schToFans.lx} y={schToFans.ly} fill={color} fontSize="11" fontFamily="var(--fb)" textAnchor="middle" dominantBaseline="central" transform={`rotate(${schToFans.angle}, ${schToFans.lx}, ${schToFans.ly})`}>Experiences</text>

            <line x1={fansToSch.x1} y1={fansToSch.y1} x2={fansToSch.x2} y2={fansToSch.y2} stroke="#9ca3af" strokeWidth="2" markerEnd="url(#arr)" />
            <text x={fansToSch.lx} y={fansToSch.ly} fill="#9ca3af" fontSize="11" fontFamily="var(--fb)" textAnchor="middle" dominantBaseline="central" transform={`rotate(${fansToSch.angle}, ${fansToSch.lx}, ${fansToSch.ly})`}>Increased loyalty</text>
          </svg>
        </div>
      </div>
    </section>
  );
}


/* ══════════════════════════════════════════════════════════════════════════ */
/*  3. HOW IT WORKS                                                          */
/* ══════════════════════════════════════════════════════════════════════════ */
function HowItWorks() {
  const { color, rewardsTitle } = useScheme();
  const ref = useFade();
  const steps = [
    { n:"1", label:"Sign up through the Homecrowd app", desc:"Create your account in the Homecrowd app. Takes under a minute." },
    { n:"2", label:"Link your card", desc:"Securely connect a debit or credit card. Verified through Visa and Mastercard." },
    { n:"3", label:"Download the browser extension", desc:"The extension helps track qualifying online transactions from participating businesses." },
    { n:"4", label:"Spend at participating businesses", desc:"No coupons, no codes. Just pay with your normal card like you always do." },
    { n:"5", label:`Earn ${rewardsTitle} automatically`, desc:"Points accrue on qualifying transactions. No extra steps at checkout." },
  ];
  return (
    <section id="how-it-works" ref={ref} className="fs py-20 md:py-28 px-6 md:px-10" style={{ fontFamily:"var(--fb)", background:"#FAFAF8" }}>
      <div className="max-w-5xl mx-auto">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <Tag>How It Works</Tag>
          <H2>Five steps. Nothing changes when you shop.</H2>
          <Sub>The entire {rewardsTitle} flow happens through seamless, everyday spend.</Sub>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 p-8 md:p-10">
          {steps.map((s,i) => (
            <div key={i}>
              <div className="flex items-start gap-3 py-5">
                <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0 mt-0.5" style={{ background:color }}>{s.n}</div>
                <div>
                  <p className="text-base font-semibold text-gray-900">{s.label}</p>
                  <p className="text-sm text-gray-500">{s.desc}</p>
                </div>
              </div>
              {i < steps.length - 1 && <div className="ml-5 w-px h-3" style={{ background:`${color}20` }} />}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}


/* ══════════════════════════════════════════════════════════════════════════ */
/*  4. WHY BUSINESSES PARTICIPATE                                            */
/* ══════════════════════════════════════════════════════════════════════════ */
function WhyBusinesses() {
  const { short } = useScheme();
  const ref = useFade();
  return (
    <section ref={ref} className="fs py-20 md:py-28 px-6 md:px-10" style={{ fontFamily:"var(--fb)", background:"#fff" }}>
      <div className="max-w-4xl mx-auto">
        <Tag>Why Businesses Participate</Tag>
        <H2>They join because they want to be part of the community.</H2>
        <div className="space-y-4 text-gray-600 leading-relaxed mt-4 mb-10">
          <p>Local businesses participate because they want to connect with the {short} community — not just run ads. They fund the program by participating, and they only contribute when fans actually spend at their location.</p>
          <p>Fans aren't forced to spend — they're given a reason to choose participating businesses over alternatives. The spending is redirected, not manufactured.</p>
        </div>
      </div>
    </section>
  );
}


/* ══════════════════════════════════════════════════════════════════════════ */
/*  5. FAN EXPERIENCE WALKTHROUGH                                            */
/* ══════════════════════════════════════════════════════════════════════════ */
function FanExperience() {
  const { rewardsTitle } = useScheme();
  const ref = useFade();
  const subs = [
    { title:"Signing Up", desc:"Download the Homecrowd app and create your account. Takes under a minute.", img:walkthroughSignup },
    { title:"Linking a Card", desc:"Securely connect a debit or credit card. Verified through Visa and Mastercard. No card data is ever shared with businesses.", img:walkthroughLinking },
    { title:`Earning ${rewardsTitle} & Tracking Progress`, desc:`When you spend at participating businesses, ${rewardsTitle} points accrue automatically — no coupons, no QR codes, no extra steps. See your points, leaderboard position, and available rewards in real time inside the app.`, img:walkthroughTracking },
  ];
  return (
    <section ref={ref} className="fs py-20 md:py-28 px-6 md:px-10" style={{ fontFamily:"var(--fb)", background:`linear-gradient(170deg, ${DARK} 0%, #262626 100%)` }}>
      <div className="max-w-5xl mx-auto">
        <Tag light>The Experience</Tag>
        <H2 light>What you'll actually see and do.</H2>
        <Sub light>A walkthrough from signup to rewards.</Sub>
        <div className="grid md:grid-cols-3 gap-8 mt-12">
          {subs.map((s,i) => (
            <div key={i} className="rounded-xl bg-white/5 border border-white/10 p-6 flex flex-col">
              <h3 className="text-white font-bold text-lg mb-4 min-h-[3.5rem] text-center" style={{ fontFamily:"var(--fd)" }}>{s.title}</h3>
              <Placeholder img={s.img} alt={s.title} dark />
              <p className="text-white/60 text-sm leading-relaxed mt-4">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}


/* ══════════════════════════════════════════════════════════════════════════ */
/*  6. REWARDS                                                               */
/* ══════════════════════════════════════════════════════════════════════════ */
function Rewards() {
  const { color, rewardsTitle } = useScheme();
  const ref = useFade();
  const tiers = [
    {
      tier: "Weekly Wins",
      freq: "Frequent",
      items: [
        "$5–$10 rewards for consistent engagement",
        "Regular giveaways tied to program activity",
        "Designed so active participants feel momentum",
      ],
    },
    {
      tier: "Competitive Layer",
      freq: "Ongoing",
      items: [
        `Leaderboards ranked by ${rewardsTitle} earned`,
        "Mid-tier rewards — merch, gift cards ($25+)",
        "Drives repeat engagement through competition",
      ],
    },
    {
      tier: "Aspirational Rewards",
      freq: "Limited",
      items: [
        "Sideline passes, locker room access, player experiences",
        "Exclusive to the program — can't get these anywhere else",
        "Behavior-driven — earned through sustained participation",
      ],
    },
  ];
  return (
    <section ref={ref} className="fs py-20 md:py-28 px-6 md:px-10" style={{ fontFamily:"var(--fb)", background:"#FAFAF8" }}>
      <div className="max-w-5xl mx-auto">
        <Tag>Rewards</Tag>
        <H2>A structured system designed to keep you engaged.</H2>
        <Sub>Three tiers that give you a reason to keep coming back.</Sub>
        <div className="grid md:grid-cols-3 gap-6 mt-12">
          {tiers.map((t,i) => (
            <div key={i} className="bg-white rounded-xl border border-gray-100 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base font-bold text-gray-900" style={{ fontFamily:"var(--fd)" }}>{t.tier}</h3>
                <span className="text-xs font-medium px-2 py-1 rounded-full" style={{ background:`${color}10`, color }}>{t.freq}</span>
              </div>
              <ul className="space-y-2">
                {t.items.map((item,j) => (
                  <li key={j} className="text-gray-600 text-sm leading-relaxed flex gap-2">
                    <span className="shrink-0 mt-1.5 w-1.5 h-1.5 rounded-full" style={{ background:color }} />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}


/* ══════════════════════════════════════════════════════════════════════════ */
/*  7. HOW WE MEASURE + OPTIMIZE                                            */
/* ══════════════════════════════════════════════════════════════════════════ */
function MeasureSuccess() {
  const { color } = useScheme();
  const ref = useFade();
  const kpis = [
    { title:"Activation", metrics:["% of testers who link a card", "% who complete their first transaction"] },
    { title:"Engagement", metrics:["Weekly active users", "Leaderboard participation rate"] },
    { title:"Spending Behavior", metrics:["Transactions per user per week", "% of spend at participating businesses"] },
    { title:"Retention", metrics:["Repeat usage week over week", "Time between transactions"] },
  ];
  return (
    <section ref={ref} className="fs py-20 md:py-28 px-6 md:px-10" style={{ fontFamily:"var(--fb)", background:"#fff" }}>
      <div className="max-w-5xl mx-auto">
        <Tag>How We Measure & Optimize</Tag>
        <H2>What we're tracking so we can keep improving.</H2>
        <Sub>These are the areas we're focused on. Your participation helps us learn what's working and what to adjust.</Sub>
        <div className="grid md:grid-cols-2 gap-6 mt-12">
          {kpis.map((k,i) => (
            <div key={i} className="rounded-xl border border-gray-100 bg-gray-50 p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold" style={{ background:color }}>{i+1}</div>
                <h3 className="text-lg font-bold text-gray-900" style={{ fontFamily:"var(--fd)" }}>{k.title}</h3>
              </div>
              <ul className="space-y-2">
                {k.metrics.map((m,j) => (
                  <li key={j} className="text-gray-600 text-sm leading-relaxed flex gap-2">
                    <span className="shrink-0 mt-1.5 w-1.5 h-1.5 rounded-full" style={{ background:color }} />
                    {m}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}


/* ══════════════════════════════════════════════════════════════════════════ */
/*  8. WHY THIS WORKS                                                        */
/* ══════════════════════════════════════════════════════════════════════════ */
function WhyThisWorks() {
  const { rewardsTitle } = useScheme();
  const ref = useFade();
  const reasons = [
    { bold:"No behavior change at checkout", desc:"Just use your normal card. No coupons, no codes, no extra steps." },
    { bold:"Rewards tied to school identity", desc:`This isn't generic cashback. ${rewardsTitle} mean something because they're connected to the program you already care about.` },
    { bold:"Spending is redirected, not created", desc:"You're already eating out. Homecrowd gives you a reason to choose participating businesses." },
    { bold:"Competition and status keep it going", desc:"Leaderboards, weekly wins, and aspirational rewards create a loop — not a one-time signup." },
  ];
  return (
    <section ref={ref} className="fs py-20 md:py-28 px-6 md:px-10" style={{ fontFamily:"var(--fb)", background:`linear-gradient(170deg, ${DARK} 0%, #262626 100%)` }}>
      <div className="max-w-4xl mx-auto">
        <Tag light>Why This Works</Tag>
        <H2 light>Why you'll actually keep using this.</H2>
        <p className="text-white/60 text-lg leading-relaxed mb-10">Most rewards programs fail because they require extra effort. This one works because the effort is zero and the incentive is personal.</p>
        <div className="space-y-4">
          {reasons.map((r,i) => (
            <div key={i} className="flex gap-4 items-start p-5 rounded-xl bg-white/5 border border-white/10">
              <span className="shrink-0 mt-1 text-lg">✓</span>
              <p className="text-white/80 text-sm leading-relaxed">
                <span className="text-white font-semibold">{r.bold}.</span> {r.desc}
              </p>
            </div>
          ))}
        </div>
        <div className="mt-10 text-center">
          <p className="text-white/50 text-sm italic" style={{ fontFamily:"var(--fd)" }}>
            This isn't about discounts. It's about identity + incentives.
          </p>
        </div>
      </div>
    </section>
  );
}


/* ══════════════════════════════════════════════════════════════════════════ */
/*  9. WHAT WE NEED FROM EARLY TESTERS                                       */
/* ══════════════════════════════════════════════════════════════════════════ */
function WhatWeNeed() {
  const ref = useFade();
  return (
    <section id="what-we-need" ref={ref} className="fs py-20 md:py-28 px-6 md:px-10" style={{ fontFamily:"var(--fb)", background:"#FAFAF8" }}>
      <div className="max-w-3xl mx-auto">
        <Tag>What We Need From You</Tag>
        <H2>Two things from our early testers.</H2>
        <Sub>You're part of a small group helping us learn and optimize. Here's how to get the most out of it.</Sub>

        <div className="space-y-6 mt-10">
          <div className="rounded-xl border border-gray-100 bg-white p-8">
            <h3 className="text-xl font-bold text-gray-900 mb-1" style={{ fontFamily:"var(--fd)" }}>Participation</h3>
            <p className="text-gray-500 text-sm mb-4">The core actions that make the program work.</p>
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                { label:"Sign up in the Homecrowd app" },
                { label:"Link your debit or credit card" },
                { label:"Download the browser extension" },
                { label:"Spend at participating businesses" },
                { label:"Check rewards, winners, and updates" },
              ].map((a,i) => (
                <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 border border-gray-100">
                  <p className="text-sm font-medium text-gray-800">{a.label}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-xl border border-gray-100 bg-white p-8">
            <h3 className="text-xl font-bold text-gray-900 mb-1" style={{ fontFamily:"var(--fd)" }}>Feedback</h3>
            <p className="text-gray-500 text-sm mb-4">Help us make this better.</p>
            <p className="text-gray-600 text-sm leading-relaxed">
              Tell us what's working, what's confusing, and what you'd want more of. We're actively adjusting the program based on what early testers experience — your feedback directly shapes what comes next.
            </p>
            <p className="text-gray-600 text-sm leading-relaxed mt-3">
              Reach out to <a href="mailto:support@gethomecrowd.com" className="underline hover:text-gray-900">support@gethomecrowd.com</a> with any questions or concerns.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}


/* ══════════════════════════════════════════════════════════════════════════ */
/*  FOOTER                                                                   */
/* ══════════════════════════════════════════════════════════════════════════ */
function Footer() {
  const { name } = useScheme();
  return (
    <footer className="py-10 px-6 md:px-10 border-t border-gray-100" style={{ fontFamily:"var(--fb)", background:"#fff" }}>
      <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
        <img src={HC_LOGO_DARK} alt="Homecrowd" className="h-7 w-auto" />
        <span className="text-gray-400 text-sm">© {new Date().getFullYear()} Homecrowd — {name} Early Access</span>
      </div>
    </footer>
  );
}


/* ══════════════════════════════════════════════════════════════════════════ */
/*  MAIN                                                                     */
/* ══════════════════════════════════════════════════════════════════════════ */
export default function Walkthrough() {
  const { schoolId } = useParams();
  const [school, setSchool] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await apiService.getOnboardingTimeline(schoolId);
        if (!cancelled) setSchool(res?.school || null);
      } catch (err) {
        console.error("Failed to fetch school for early access overview:", err);
        if (!cancelled) setError("School not found.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    if (schoolId) load();
    else { setLoading(false); setError("Missing school id."); }
    return () => { cancelled = true; };
  }, [schoolId]);

  const scheme = useMemo(() => buildScheme(school), [school]);
  const styleString = useMemo(() => buildStyles(scheme.color), [scheme.color]);

  if (loading) {
    return (
      <div style={{ minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", background:"#fff" }}>
        <span style={{ color:"#666", fontSize:"16px", fontFamily:"system-ui, sans-serif" }}>Loading...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", background:"#fff" }}>
        <span style={{ color:"#cc3333", fontSize:"16px", fontFamily:"system-ui, sans-serif" }}>{error}</span>
      </div>
    );
  }

  return (
    <SchemeContext.Provider value={scheme}>
      <div className="min-h-screen" style={{ background:"#fff" }}>
        <style>{styleString}</style>
        <Nav />
        <Hero />
        <BigIdea />
        <HowItWorks />
        <WhyBusinesses />
        <FanExperience />
        <Rewards />
        <MeasureSuccess />
        <WhyThisWorks />
        <WhatWeNeed />
        <Footer />
      </div>
    </SchemeContext.Provider>
  );
}
